import {
  formatReceiptDate,
  formatReceiptMoney,
  referenceLabel,
  type CanonicalReference,
  type ReceiptOperation,
  type ReceiptRow,
  type ReceiptTranslate,
} from "./receiptRows";

export interface ReceiptPdfStrings {
  documentTitle: string;
  youTransferred: string;
  transactionId: string;
  footerNote: string;
}

export interface BuildReceiptPdfInput {
  operation: ReceiptOperation;
  rows: ReceiptRow[];
  prominentRefs: CanonicalReference[];
  partnerName?: string;
  strings: ReceiptPdfStrings;
  t: ReceiptTranslate;
  lang: string;
}

const PAGE_WIDTH = 210;
const MARGIN = 18;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const INK = "#252525";
const MUTED = "#5a5a5a";

/**
 * Vector receipt from operation data — no DOM raster. jsPDF is imported
 * dynamically so it stays out of the widget's initial bundle.
 */
export const buildReceiptPdf = async ({
  operation,
  rows,
  prominentRefs,
  partnerName,
  strings,
  t,
  lang,
}: BuildReceiptPdfInput): Promise<Blob> => {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const source = operation.sourceCurrency;
  let y = 22;

  const writeCentered = (
    text: string,
    size: number,
    style: "normal" | "bold" = "normal",
    color = INK
  ) => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    doc.setTextColor(color);
    doc.text(text, PAGE_WIDTH / 2, y, { align: "center" });
    y += size * 0.45 + 2;
  };

  if (partnerName) {
    writeCentered(partnerName, 11, "normal", MUTED);
  }
  writeCentered(strings.documentTitle, 14, "bold");
  writeCentered(formatReceiptDate(new Date(operation.createdAt)), 10, "normal", MUTED);
  y += 4;

  writeCentered(strings.youTransferred, 10, "normal", MUTED);
  writeCentered(formatReceiptMoney(operation.sendAmount, source, lang), 22, "bold");
  writeCentered(strings.transactionId, 10, "bold");
  writeCentered(operation.operationId.slice(0, 8).toUpperCase(), 11);

  for (const ref of prominentRefs) {
    y += 4;
    const label = referenceLabel(ref.type, ref.label, t, lang);
    const boxHeight = 22;
    doc.setDrawColor("#eeeeee");
    doc.setLineWidth(0.4);
    doc.roundedRect(MARGIN, y, CONTENT_WIDTH, boxHeight, 2, 2, "S");
    const innerY = y + 8;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(MUTED);
    doc.text(label, PAGE_WIDTH / 2, innerY, { align: "center" });
    doc.setFont("courier", "bold");
    doc.setFontSize(16);
    doc.setTextColor(INK);
    doc.text(ref.value, PAGE_WIDTH / 2, innerY + 9, { align: "center" });
    y += boxHeight + 4;
  }

  y += 4;
  doc.setDrawColor("#eeeeee");
  doc.setLineWidth(0.2);
  doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
  y += 8;

  for (const row of rows) {
    if (y > 270) {
      doc.addPage();
      y = 22;
    }
    doc.setFont("helvetica", row.emphasis ? "bold" : "normal");
    doc.setFontSize(10);
    doc.setTextColor(INK);
    const valueLines = doc.splitTextToSize(row.value, CONTENT_WIDTH * 0.5);
    doc.text(row.label, MARGIN, y);
    doc.text(valueLines, PAGE_WIDTH - MARGIN, y, { align: "right" });
    y += Math.max(6, valueLines.length * 5);
  }

  y += 10;
  if (y > 275) {
    doc.addPage();
    y = 22;
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(MUTED);
  const footerLines = doc.splitTextToSize(strings.footerNote, CONTENT_WIDTH);
  doc.text(footerLines, MARGIN, y);

  return doc.output("blob");
};
